import { CreateOrganizationRequest, CreateOrganizationResponse, GetOrganizationResponse, GetPublicOrganizationResponse, OrganizationDemographics, OrganizationTeamMember, TelemetryEntry, TranslationElement, TranslationSet, CreateApiKeyRequest, AccountInformation, ApiKeyInfo } from '../../types';
import { buildQueryString, get, logWithAmplitude, post, put, throwHttpError, del } from './api';

import accountInfo from '../state/account-info';

export async function updateOrganizationDemographics(
    amplitude_callsite: string,
    organization_id: string,
    demographics: OrganizationDemographics,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/organizations/${organization_id}/demographics`, put(demographics));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the organization demographics.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Organization Demographics Updated',
        Object.assign({}, { organization_id }, additional_logged_parameters)
    );
}

export async function updateOrganization(
    amplitude_callsite: string,
    organization_id: string,
    organization: CreateOrganizationRequest,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/organizations/${organization_id}`, put(organization));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the organization.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Organization Updated',
        Object.assign({}, { organization_id }, additional_logged_parameters)
    );
}

export async function getOrganization(amplitude_callsite: string, organization_id: string) {
    const response = await fetch(`/api/organizations/${organization_id}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the organization.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Organization', {
        organization_id,
    });
    return await (response.json() as Promise<GetOrganizationResponse>);
}

export async function getPublicOrganizations(amplitude_callsite: string, organization_ids: string[]) {
    // org ids to comma separated string
    const org_ids = organization_ids.map((org_id) => org_id.toLowerCase());
    const unique_org_ids = [...new Set(org_ids)];
    const organization_ids_string = unique_org_ids.join(',');
    // url encode
    const queryString = buildQueryString({ organization_ids: organization_ids_string });
    const response = await fetch(`/api/organizations/public${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the organization.', response);
    }
    logWithAmplitude(amplitude_callsite, 'Get Organizations', {
        organization_ids: organization_ids.join(','), // Fixed: added key name
    });
    return await (response.json() as Promise<GetPublicOrganizationResponse[]>);
}

export async function getPublicOrganizationByDomain(domain: string) {
    // url to lowercase and url encode
    const encoded_domain = encodeURIComponent(domain.toLowerCase());
    const response = await fetch(`/api/organizations/url/${encoded_domain}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the organization by domain.', response);
    }

    return await (response.json() as Promise<GetPublicOrganizationResponse>);
}

export async function getOrganizationBySSO(amplitude_callsite: string, sso: string) {
    const response = await fetch(`/api/organizations/sso/${sso}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the organization by sso.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Organization', { sso });
    return await (response.json() as Promise<GetOrganizationResponse>);
}

export async function createOrganization(
    amplitude_callsite: string,
    organization: CreateOrganizationRequest,
    additional_logged_parameters: Object = {}
) {
    const email = accountInfo.email;
    const queryString = buildQueryString({ email });
    const response = await fetch(`/api/organizations${queryString}`, post(organization));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the organization.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Organization Created', additional_logged_parameters);
    return await (response.json() as Promise<CreateOrganizationResponse>);
}

export async function migrateTopicToOrganization(
    amplitude_callsite: string,
    organization_id: string,
    topic_id: string,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/organizations/${organization_id}/topics/${topic_id}`, put({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while migrating the topic to the organization.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Migrated to Organization',
        Object.assign({}, { organization_id, topic_id }, additional_logged_parameters)
    );
}

export async function addTranslation(
    amplitude_callsite: string,
    organization_id: string,
    translation: TranslationElement,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/translations/${organization_id}`, post(translation));
    if (!response.ok) {
        await throwHttpError('An error occurred while adding the translation.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Translation Added', additional_logged_parameters);
}

export async function getAllTranslations(amplitude_callsite: string, organization_id: string) {
    let response: TranslationSet[];
    try{
        const response_raw = await fetch(`/api/translations/${organization_id}`, get());
        response = await response_raw.json();
    } catch (error) {
        response = [] as TranslationSet[];
    }

    logWithAmplitude(amplitude_callsite, 'Get Translations', { organization_id });
    return response;
}

export async function getOrgTeamMembers(
    amplitude_callsite: string,
    organization_id: string,
    additional_logged_parameters: Object = {}
)
 {
    const response = await fetch(`/api/organizations/${organization_id}/team`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting the team members.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Team Members', additional_logged_parameters);
    return await (response.json() as Promise<OrganizationTeamMember[]>);
}


export async function getAllRendersForOrganization(
    amplitude_callsite: string,
    organization_id: string,
) {
    const response = await fetch(`/api/organizations/${organization_id}/renders`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the renders for topic.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get All Renders For Organization', {
        organization_id,
    });
    return await (response.json() as Promise<TelemetryEntry[]>);
}

export async function createOrganizationApiKey(
    amplitude_callsite: string,
    organization_id: string,
    request: CreateApiKeyRequest,
    additional_logged_parameters: Object = {}
): Promise<AccountInformation> {
    const response = await fetch(`/api/organizations/${organization_id}/api_token`, post(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the API key.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Organization API Key Created',
        Object.assign({}, { organization_id, access_level: request.access_level }, additional_logged_parameters)
    );
    return await (response.json() as Promise<AccountInformation>);
}

export async function listOrganizationApiKeys(
    amplitude_callsite: string,
    organization_id: string,
    additional_logged_parameters: Object = {}
): Promise<ApiKeyInfo[]> {
    const response = await fetch(`/api/organizations/${organization_id}/api_tokens`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting the API keys.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'List Organization API Keys',
        Object.assign({}, { organization_id }, additional_logged_parameters)
    );
    return await (response.json() as Promise<ApiKeyInfo[]>);
}

export async function deleteOrganizationApiKey(
    amplitude_callsite: string,
    organization_id: string,
    api_key_id: string,
    additional_logged_parameters: Object = {}
): Promise<void> {
    const queryString = buildQueryString({ api_id: api_key_id });
    const response = await fetch(`/api/organizations/${organization_id}/api_token${queryString}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the API key.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Organization API Key Deleted',
        Object.assign({}, { organization_id, api_key_id }, additional_logged_parameters)
    );
}